import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, ListChecks } from 'lucide-react';
import * as tareasApi from '@/api/tareas-cliente.api';
import { Card, CardBody } from '@/components/ui/Card';

/**
 * La tarjeta "Lo que falta configurar" del resumen del día.
 *
 * Aparece mientras quede algo: una tarea del sistema sin completar o una nota
 * propia sin tachar. Cuando todo está hecho, o cuando alguien la oculta desde
 * la pantalla de tareas, desaparece sola. La pantalla sigue en el menú.
 */
export function TareasPendientesCard() {
  const { data } = useQuery({ queryKey: ['tareas-cliente'], queryFn: tareasApi.obtener });
  if (!data || data.oculto) return null;

  const pendientes = data.tareas.filter((t) => !t.hecho);
  const personalesAbiertas = data.personales.filter((p) => !p.hecho);
  if (pendientes.length === 0 && personalesAbiertas.length === 0) return null;

  const { hechas, total } = data.resumen;
  const pct = total > 0 ? Math.round((hechas / total) * 100) : 100;

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-brand-600" />
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Lo que falta configurar
            </h2>
          </div>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {hechas} de {total} listas
          </span>
        </div>

        <div
          className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="h-full rounded-full bg-brand-600" style={{ width: `${pct}%` }} />
        </div>

        <ul className="space-y-1.5">
          {pendientes.slice(0, 3).map((t) => (
            <li
              key={t.id}
              className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />
              <span>{t.titulo}</span>
            </li>
          ))}
          {pendientes.length > 3 && (
            <li className="pl-6 text-xs text-slate-400">y {pendientes.length - 3} más</li>
          )}
          {personalesAbiertas.length > 0 && (
            <li className="flex items-start gap-2 text-sm text-slate-500 dark:text-slate-400">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />
              <span>
                {personalesAbiertas.length} nota{personalesAbiertas.length === 1 ? '' : 's'} propia
                {personalesAbiertas.length === 1 ? '' : 's'} sin tachar
              </span>
            </li>
          )}
        </ul>

        <Link
          to="/tareas"
          className="inline-block text-sm font-medium text-brand-600 hover:text-brand-700"
        >
          Completar ahora
        </Link>
      </CardBody>
    </Card>
  );
}
